import React, { useState } from 'react';
import config from '../config';
import { formatDateSk, daysLabelSk } from '../utils/dates';
import { formatEur } from '../utils/pricing';
import { validateDiscountCode } from '../utils/discounts';

/**
 * Cenová kalkulačka: rozpis dní, doplnky, zľavový kód a súčty.
 * Zobrazuje sa v lepiacej karte (desktop) aj nad formulárom (mobil).
 */
export default function BookingSummary({
  startKey,
  endKey,
  quote,
  extraIds,
  onToggleExtra,
  discount,
  onDiscountChange,
}) {
  const [codeInput, setCodeInput] = useState('');
  // null | 'checking' | 'ok' | 'invalid' | 'inactive' | 'error'
  const [codeStatus, setCodeStatus] = useState(null);

  const applyCode = async () => {
    setCodeStatus('checking');
    const result = await validateDiscountCode(codeInput);
    if (result.status === 'ok') {
      onDiscountChange({
        code: codeInput.trim().toUpperCase(),
        percent: result.percent,
        voucher: result.voucher,
      });
    } else {
      onDiscountChange({ code: '', percent: 0, voucher: 0 });
    }
    setCodeStatus(result.status);
  };

  if (!startKey || !endKey || !quote) {
    return (
      <div className="summary summary--empty">
        <h3>Tvoja rezervácia</h3>
        <p>
          Vyber si v kalendári dátum vyzdvihnutia a vrátenia – cenu spočítame
          okamžite.
        </p>
        <p className="summary__min">Minimálny prenájom: {config.minDays} dni</p>
      </div>
    );
  }

  return (
    <div className="summary">
      <h3>Tvoja rezervácia</h3>

      <div className="summary__dates">
        <div>
          <span className="summary__label">Vyzdvihnutie</span>
          <strong>{formatDateSk(startKey)}</strong>
        </div>
        <div>
          <span className="summary__label">Vrátenie</span>
          <strong>{formatDateSk(endKey)}</strong>
        </div>
      </div>

      <dl className="summary__rows">
        {quote.rateGroups.map((group, index) => (
          <div className="summary__row" key={`${group.rate}-${index}`}>
            <dt>
              {group.count} × {formatEur(group.rate)}
              {group.season === 'high' && group.rate === config.highSeasonLongStayPrice && (
                <span className="summary__tag">zvýhodnené</span>
              )}
            </dt>
            <dd>{formatEur(group.count * group.rate)}</dd>
          </div>
        ))}
      </dl>

      <fieldset className="summary__extras">
        <legend>Voliteľné doplnky</legend>
        {config.extras.map((extra) => (
          <label key={extra.id} className="summary__extra">
            <input
              type="checkbox"
              checked={extraIds.includes(extra.id)}
              onChange={() => onToggleExtra(extra.id)}
            />
            <span>
              {extra.label}
              <small>
                {formatEur(extra.perDay)}/deň, max. {formatEur(extra.cap)}
              </small>
            </span>
          </label>
        ))}
      </fieldset>

      <div className="summary__code">
        <label htmlFor="discount-code">Zľavový kód / voucher</label>
        <div className="summary__code-row">
          <input
            id="discount-code"
            type="text"
            value={codeInput}
            onChange={(event) => {
              setCodeInput(event.target.value);
              setCodeStatus(null);
            }}
            placeholder="napr. VITAJ10"
            autoComplete="off"
          />
          <button
            type="button"
            className="btn btn--ghost"
            onClick={applyCode}
            disabled={codeStatus === 'checking'}
          >
            {codeStatus === 'checking' ? 'Overujem…' : 'Použiť'}
          </button>
        </div>
        {codeStatus === 'ok' && (
          <p className="summary__code-msg summary__code-msg--ok" role="status">
            Kód {discount.code} uplatnený:{' '}
            {[
              discount.voucher > 0 ? `-${formatEur(discount.voucher)}` : null,
              discount.percent > 0 ? `-${discount.percent} %` : null,
            ]
              .filter(Boolean)
              .join(' + ')}
          </p>
        )}
        {codeStatus === 'invalid' && (
          <p className="summary__code-msg summary__code-msg--err" role="alert">
            Tento kód nepoznáme.
          </p>
        )}
        {codeStatus === 'inactive' && (
          <p className="summary__code-msg summary__code-msg--err" role="alert">
            Tento kupón už nie je platný.
          </p>
        )}
        {codeStatus === 'error' && (
          <p className="summary__code-msg summary__code-msg--err" role="alert">
            Kód sa nepodarilo overiť. Skús to o chvíľu znova.
          </p>
        )}
      </div>

      <dl className="summary__rows summary__totals">
        <div className="summary__row">
          <dt>Prenájom ({daysLabelSk(quote.daysCount)})</dt>
          <dd>{formatEur(quote.rentalTotal)}</dd>
        </div>
        {quote.extras.map((extra) => (
          <div className="summary__row" key={extra.id}>
            <dt>{extra.label}</dt>
            <dd>{formatEur(extra.total)}</dd>
          </div>
        ))}
        {quote.voucherAmount > 0 && (
          <div className="summary__row summary__row--discount">
            <dt>Voucher {discount.code}</dt>
            <dd>-{formatEur(quote.voucherAmount)}</dd>
          </div>
        )}
        {quote.discountAmount > 0 && (
          <div className="summary__row summary__row--discount">
            <dt>Zľava {quote.discountPercent} %</dt>
            <dd>-{formatEur(quote.discountAmount)}</dd>
          </div>
        )}
        <div className="summary__row summary__row--total">
          <dt>Cena prenájmu spolu</dt>
          <dd>{formatEur(quote.total)}</dd>
        </div>
        <div className="summary__row summary__row--deposit">
          <dt>Vratná záloha</dt>
          <dd>{formatEur(quote.deposit)}</dd>
        </div>
      </dl>

      <p className="summary__note">
        Záloha sa vracia v plnej výške po vrátení nepoškodeného vozidla.
        Odoslaním formulára vzniká nezáväzná požiadavka – termín potvrdíme
        e-mailom alebo telefonicky.
      </p>
    </div>
  );
}
